import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
  Cell,
} from 'recharts';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';

// Same fixed order used on the admin Dashboard's Ticket Health chart, kept
// consistent here: Open -> In Progress -> Resolved -> Closed everywhere
// (legend, tooltip, and the stack itself reading top-to-bottom).
const STATUS_ORDER = { open: 0, inProgress: 1, resolved: 2, closed: 3 };
const STATUS_LEGEND = [
  { value: 'Open', color: '#f59e0b' },
  { value: 'In Progress', color: '#0ea5e9' },
  { value: 'Resolved', color: '#10b981' },
  { value: 'Closed', color: '#94a3b8' },
];

function StatusLegend() {
  return (
    <ul className="mt-2 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
      {STATUS_LEGEND.map((item) => (
        <li key={item.value} className="flex items-center gap-2 text-sm text-gray-600">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: item.color }} />
          {item.value}
        </li>
      ))}
    </ul>
  );
}

const PRIORITY_COLORS = { low: '#10b981', medium: '#f59e0b', high: '#ef4444' };
const PRIORITY_LABELS = { low: 'Low', medium: 'Medium', high: 'High' };

const AGING_BUCKETS = [
  { key: 'd0to3', label: '0-3 days', color: '#10b981' },
  { key: 'd4to7', label: '4-7 days', color: '#f59e0b' },
  { key: 'd8to14', label: '8-14 days', color: '#f97316' },
  { key: 'd15plus', label: '15+ days', color: '#ef4444' },
];

function courseBaseName(course) {
  return course?.courseShortName || course?.shortName || course?.courseName || course?.fullName || 'Course';
}

// Adaptive hours -> "2.4 days" / "18.3 hrs" formatting; null means "no
// tickets with a timestamp for this metric yet" rather than zero.
function formatHours(hours) {
  if (hours === null || hours === undefined || Number.isNaN(hours)) return 'No data yet';
  if (hours < 24) return `${hours.toFixed(1)} hrs`;
  return `${(hours / 24).toFixed(1)} days`;
}

function formatDateShort(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short' }).format(d);
}

function KpiCard({ label, value, sub, accent }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">{label}</p>
      <p className={`mt-1.5 text-2xl font-bold ${accent || 'text-gray-900'}`}>{value}</p>
      {sub && <p className="mt-1 text-xs text-gray-400">{sub}</p>}
    </div>
  );
}

function ChartCard({ title, subtitle, children, empty }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-4">
        <h3 className="text-base font-bold text-gray-800">{title}</h3>
        {subtitle && <p className="mt-0.5 text-xs text-gray-400">{subtitle}</p>}
      </div>
      {empty ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-10 text-center text-sm text-gray-400">
          {empty}
        </div>
      ) : (
        children
      )}
    </div>
  );
}

export default function TicketAnalysis() {
  const navigate = useNavigate();

  const [courses, setCourses] = useState([]);
  const [selectedCourseIds, setSelectedCourseIds] = useState(['all']);
  const [courseDropdownOpen, setCourseDropdownOpen] = useState(false);
  const [courseSearchQuery, setCourseSearchQuery] = useState('');
  const courseDropdownRef = useRef(null);
  // Defaults to All Time -- most resolved/closed tickets in a mature dataset
  // are older than 30 days, so a "last 30 days" default made those KPIs and
  // charts look empty even though the data exists. Date filters are still
  // available for narrowing down.
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [analysis, setAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(true);
  const [analysisError, setAnalysisError] = useState(null);

  const [courseSummary, setCourseSummary] = useState([]);
  const [courseSummaryLoading, setCourseSummaryLoading] = useState(true);
  const [courseSummaryError, setCourseSummaryError] = useState(null);

  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(true);
  const [leaderboardError, setLeaderboardError] = useState(null);

  useEffect(() => {
    axiosInstance.get('/admin/me/coordinate-courses').then(({ data }) => {
      setCourses(Object.values(data?.coordinateCourses || {}));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!courseDropdownOpen) return undefined;
    const handleClick = (e) => {
      if (courseDropdownRef.current && !courseDropdownRef.current.contains(e.target)) {
        setCourseDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [courseDropdownOpen]);

  const filterParams = useMemo(() => ({
    courseIds: selectedCourseIds.includes('all') ? 'all' : selectedCourseIds.join(','),
    startDate,
    endDate,
  }), [selectedCourseIds, startDate, endDate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setAnalysisLoading(true);
      setAnalysisError(null);
      try {
        const { data } = await axiosInstance.get('/tickets/analysis', { params: filterParams });
        if (!cancelled) setAnalysis(data);
      } catch (err) {
        if (!cancelled) setAnalysisError(err?.response?.data?.message || 'Failed to load ticket analysis.');
      } finally {
        if (!cancelled) setAnalysisLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [filterParams]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCourseSummaryLoading(true);
      setCourseSummaryError(null);
      try {
        const { data } = await axiosInstance.get('/tickets/coordinator-summary', { params: filterParams });
        if (!cancelled) setCourseSummary(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) setCourseSummaryError(err?.response?.data?.message || 'Failed to load course-wise status.');
      } finally {
        if (!cancelled) setCourseSummaryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [filterParams]);

  // Staff leaderboard is intentionally a fixed "last 30 days" window
  // (same as the admin Dashboard's version) so it isn't re-fetched per
  // filter change -- only loaded once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLeaderboardLoading(true);
      setLeaderboardError(null);
      try {
        const { data } = await axiosInstance.get('/tickets/staff-leaderboard');
        if (!cancelled) setLeaderboard(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) setLeaderboardError(err?.response?.data?.message || 'Failed to load the staff leaderboard.');
      } finally {
        if (!cancelled) setLeaderboardLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const courseName = useMemo(() => {
    if (selectedCourseIds.includes('all') || selectedCourseIds.length === 0) return 'All Courses';
    if (selectedCourseIds.length === 1) {
      const match = courses.find((c) => String(c.courseId) === selectedCourseIds[0]);
      return match ? courseBaseName(match) : 'Course';
    }
    return `${selectedCourseIds.length} courses selected`;
  }, [selectedCourseIds, courses]);

  const kpis = analysis?.kpis;
  const priorityBreakdown = useMemo(
    () => (analysis?.priorityBreakdown || []).map((p) => ({
      priority: PRIORITY_LABELS[p.priority] || p.priority,
      total: p.total,
      fill: PRIORITY_COLORS[p.priority] || '#94a3b8',
    })),
    [analysis]
  );
  const dailyTrend = useMemo(
    () => (analysis?.dailyTrend || []).map((d) => ({ ...d, label: formatDateShort(d.date) })),
    [analysis]
  );
  const agingData = useMemo(() => {
    const aging = analysis?.openAging;
    if (!aging) return [];
    return AGING_BUCKETS.map((b) => ({ label: b.label, total: aging[b.key] || 0, fill: b.color }));
  }, [analysis]);

  return (
    <div className="min-h-screen bg-slate-50 px-6 py-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-5 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/admin/dashboard'))}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
            Back
          </button>
          <div>
            <h1 className="text-lg font-bold text-slate-800">Ticket Analysis</h1>
            <p className="text-xs text-slate-500">Resolution performance, course-wise health, and volume trends across the support ticket system</p>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            <div className="relative" ref={courseDropdownRef}>
              <label className="mb-1.5 block text-xs font-semibold text-slate-500">Course</label>
              <button
                type="button"
                onClick={() => setCourseDropdownOpen((v) => !v)}
                className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-left text-sm text-slate-700 outline-none focus:border-violet-400"
              >
                <span className="truncate">{courseName}</span>
                <svg className="h-4 w-4 shrink-0 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </button>

              {courseDropdownOpen && (
                <div className="absolute left-0 top-full z-20 mt-1 w-72 rounded-lg border border-slate-200 bg-white shadow-lg">
                  <div className="border-b border-slate-100 p-2">
                    <input
                      type="text"
                      value={courseSearchQuery}
                      onChange={(e) => setCourseSearchQuery(e.target.value)}
                      placeholder="Search courses…"
                      className="w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm text-slate-700 outline-none focus:border-violet-400"
                      autoFocus
                    />
                  </div>
                  <div className="max-h-64 overflow-y-auto p-1">
                    {(() => {
                      const visible = courses.filter((c) => courseBaseName(c).toLowerCase().includes(courseSearchQuery.trim().toLowerCase()));
                      const visibleIds = visible.map((c) => String(c.courseId));
                      const allChecked = visibleIds.length > 0 && (selectedCourseIds.includes('all') || visibleIds.every((id) => selectedCourseIds.includes(id)));
                      return (
                        <>
                          <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-slate-50">
                            <input
                              type="checkbox"
                              checked={allChecked}
                              onChange={(e) => setSelectedCourseIds(e.target.checked ? ['all'] : [])}
                              className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                            />
                            <span className="font-semibold text-slate-700">Select All</span>
                          </label>
                          <div className="my-1 border-t border-slate-100" />
                          {visible.length === 0 ? (
                            <p className="px-2 py-3 text-center text-xs text-slate-400">No courses match.</p>
                          ) : (
                            visible.map((c) => {
                              const idStr = String(c.courseId);
                              const checked = selectedCourseIds.includes('all') || selectedCourseIds.includes(idStr);
                              return (
                                <label key={idStr} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-slate-50">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedCourseIds((prev) => [...new Set([...prev.filter((x) => x !== 'all'), idStr])]);
                                      } else {
                                        setSelectedCourseIds((prev) => (prev.includes('all') ? visibleIds.filter((x) => x !== idStr) : prev.filter((x) => x !== idStr)));
                                      }
                                    }}
                                    className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                                  />
                                  <span className="truncate text-slate-700">{courseBaseName(c)}</span>
                                </label>
                              );
                            })
                          )}
                        </>
                      );
                    })()}
                  </div>
                  <div className="border-t border-slate-100 p-2">
                    <button
                      type="button"
                      onClick={() => setCourseDropdownOpen(false)}
                      className="w-full rounded-md bg-violet-600 py-1.5 text-sm font-semibold text-white hover:bg-violet-700"
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-500">Start Date <span className="font-normal text-slate-400">(blank = all time)</span></label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 outline-none focus:border-violet-400" />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-500">End Date <span className="font-normal text-slate-400">(blank = all time)</span></label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 outline-none focus:border-violet-400" />
            </div>
          </div>
          <p className="mt-3 text-[11px] text-slate-400">
            Showing all-time data by default. Course and date filters apply to every chart below except the staff leaderboard, which is always the trailing 30 days.
          </p>
        </div>

        {analysisError && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{analysisError}</div>
        )}

        {/* KPI row */}
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
          <KpiCard label="Total" value={analysisLoading ? '…' : kpis?.total ?? 0} />
          <KpiCard label="Open" value={analysisLoading ? '…' : kpis?.open ?? 0} accent="text-amber-600" />
          <KpiCard label="In Progress" value={analysisLoading ? '…' : kpis?.inProgress ?? 0} accent="text-sky-600" />
          <KpiCard label="Resolved" value={analysisLoading ? '…' : kpis?.resolved ?? 0} accent="text-emerald-600" />
          <KpiCard label="Closed" value={analysisLoading ? '…' : kpis?.closed ?? 0} accent="text-slate-600" />
          <KpiCard
            label="Avg. Resolve Time"
            value={analysisLoading ? '…' : formatHours(kpis?.avgResolveHours)}
          />
          <KpiCard
            label="Avg. Close Time"
            value={analysisLoading ? '…' : formatHours(kpis?.avgCloseHours)}
            sub="Based on last status update"
          />
          <KpiCard
            label={`SLA (${kpis?.slaTargetHours ?? 72}h)`}
            value={analysisLoading ? '…' : (kpis?.slaComplianceRate === null || kpis?.slaComplianceRate === undefined ? 'No data yet' : `${kpis.slaComplianceRate}%`)}
            sub="Resolved within target"
          />
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Course-wise ticket status */}
          <div className="lg:col-span-2">
            <ChartCard
              title="Course-wise Ticket Status"
              subtitle="Every course's tickets by current status"
              empty={
                courseSummaryError
                  ? courseSummaryError
                  : !courseSummaryLoading && courseSummary.length === 0
                    ? 'No tickets match the current filters.'
                    : null
              }
            >
              {courseSummaryLoading ? (
                <div className="flex items-end justify-around gap-4" style={{ height: 280 }}>
                  {[120, 200, 90, 160, 140].map((h, i) => (
                    <div key={i} className="w-12 animate-pulse rounded-t-lg bg-violet-100" style={{ height: h }} />
                  ))}
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={340}>
                  <BarChart data={courseSummary} margin={{ top: 20, right: 20, left: 10, bottom: 70 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="courseShortName" angle={-30} textAnchor="end" height={90} interval={0} tick={{ fontSize: 11, fill: '#6B7280' }} />
                    <YAxis allowDecimals={false} tick={{ fill: '#6B7280' }} />
                    <Tooltip
                      cursor={{ fill: 'rgba(139, 92, 246, 0.06)' }}
                      labelFormatter={(label, payload) => payload?.[0]?.payload?.courseName || label}
                      itemSorter={(item) => STATUS_ORDER[item.dataKey] ?? 99}
                    />
                    <Legend content={<StatusLegend />} />
                    <Bar dataKey="closed" name="Closed" stackId="tickets" fill="#94a3b8" />
                    <Bar dataKey="resolved" name="Resolved" stackId="tickets" fill="#10b981" />
                    <Bar dataKey="inProgress" name="In Progress" stackId="tickets" fill="#0ea5e9" />
                    <Bar dataKey="open" name="Open" stackId="tickets" fill="#f59e0b" radius={[4, 4, 0, 0]}>
                      <LabelList dataKey="total" position="top" style={{ fill: '#6B7280', fontSize: 11, fontWeight: 600 }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>

          {/* Daily trend */}
          <ChartCard
            title="Daily Ticket Volume"
            subtitle={!startDate && !endDate ? 'Trailing 30 days' : `${startDate} to ${endDate}`}
            empty={!analysisLoading && dailyTrend.length === 0 ? 'No tickets created in this window.' : null}
          >
            {analysisLoading ? (
              <div className="h-64 animate-pulse rounded-xl bg-gray-50" />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={dailyTrend} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6B7280' }} />
                  <YAxis allowDecimals={false} tick={{ fill: '#6B7280' }} />
                  <Tooltip labelFormatter={(_, payload) => payload?.[0]?.payload?.date || ''} />
                  <Line type="monotone" dataKey="total" name="Tickets created" stroke="#6366f1" strokeWidth={2.5} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* Open ticket aging */}
          <ChartCard
            title="Open Ticket Aging"
            subtitle="How long currently-open tickets have been waiting"
            empty={!analysisLoading && (kpis?.open ?? 0) === 0 ? 'No open tickets right now. 🎉' : null}
          >
            {analysisLoading ? (
              <div className="h-64 animate-pulse rounded-xl bg-gray-50" />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={agingData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6B7280' }} />
                  <YAxis allowDecimals={false} tick={{ fill: '#6B7280' }} />
                  <Tooltip />
                  <Bar dataKey="total" name="Open tickets" radius={[4, 4, 0, 0]}>
                    {agingData.map((entry) => (
                      <Cell key={entry.label} fill={entry.fill} />
                    ))}
                    <LabelList dataKey="total" position="top" style={{ fill: '#6B7280', fontSize: 11, fontWeight: 600 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* Category breakdown */}
          <ChartCard
            title="Category Breakdown"
            subtitle="Ticket volume by category, across the selected courses"
            empty={!analysisLoading && (analysis?.categoryBreakdown || []).length === 0 ? 'No tickets match the current filters.' : null}
          >
            {analysisLoading ? (
              <div className="h-64 animate-pulse rounded-xl bg-gray-50" />
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(260, (analysis?.categoryBreakdown?.length || 0) * 32)}>
                <BarChart
                  data={analysis?.categoryBreakdown || []}
                  layout="vertical"
                  margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis type="number" allowDecimals={false} tick={{ fill: '#6B7280' }} />
                  <YAxis type="category" dataKey="categoryName" width={140} tick={{ fontSize: 11, fill: '#6B7280' }} />
                  <Tooltip />
                  <Bar dataKey="total" name="Tickets" fill="#8b5cf6" radius={[0, 4, 4, 0]}>
                    <LabelList dataKey="total" position="right" style={{ fill: '#6B7280', fontSize: 11, fontWeight: 600 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* Priority breakdown */}
          <ChartCard
            title="Priority Breakdown"
            subtitle="Ticket volume by priority"
            empty={!analysisLoading && priorityBreakdown.length === 0 ? 'No tickets match the current filters.' : null}
          >
            {analysisLoading ? (
              <div className="h-64 animate-pulse rounded-xl bg-gray-50" />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={priorityBreakdown} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="priority" tick={{ fontSize: 12, fill: '#6B7280' }} />
                  <YAxis allowDecimals={false} tick={{ fill: '#6B7280' }} />
                  <Tooltip />
                  <Bar dataKey="total" name="Tickets" radius={[4, 4, 0, 0]}>
                    {priorityBreakdown.map((entry) => (
                      <Cell key={entry.priority} fill={entry.fill} />
                    ))}
                    <LabelList dataKey="total" position="top" style={{ fill: '#6B7280', fontSize: 11, fontWeight: 600 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* Staff leaderboard */}
          <div className="lg:col-span-2">
            <ChartCard
              title="🏆 Top Problem Solvers"
              subtitle="Tickets resolved in the last 30 days, ranked highest to lowest"
              empty={
                leaderboardError
                  ? leaderboardError
                  : !leaderboardLoading && leaderboard.length === 0
                    ? 'No tickets have been resolved in the last 30 days yet.'
                    : null
              }
            >
              {leaderboardLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((k) => (
                    <div key={k} className="flex items-center gap-4 rounded-xl border border-dashed border-amber-200 bg-amber-50/40 p-4">
                      <div className="h-10 w-10 animate-pulse rounded-full bg-gray-200" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-40 animate-pulse rounded bg-gray-200" />
                        <div className="h-2 w-full animate-pulse rounded-full bg-gray-100" />
                      </div>
                      <div className="h-6 w-10 animate-pulse rounded-full bg-gray-200" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {leaderboard.map((staff, index) => {
                    const topCount = leaderboard[0]?.resolvedCount || 1;
                    const barPct = Math.max(6, Math.round((staff.resolvedCount / topCount) * 100));
                    const rankBadge = index === 0 ? 'bg-amber-100 text-amber-700' : index === 1 ? 'bg-gray-200 text-gray-700' : index === 2 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-500';
                    const initials = (staff.name || '?').trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('');
                    return (
                      <div key={staff.staffId} className="flex items-center gap-4 rounded-xl border border-gray-200 p-4 transition hover:shadow-sm">
                        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${rankBadge}`}>{index + 1}</span>
                        {staff.imagePath ? (
                          <img src={getImageURL(staff.imagePath)} alt={staff.name} className="h-10 w-10 shrink-0 rounded-full object-cover" />
                        ) : (
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-violet-100 text-sm font-bold text-violet-700">{initials}</div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-gray-800">{staff.name}</p>
                          <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                            <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-500" style={{ width: `${barPct}%` }} />
                          </div>
                        </div>
                        <span className="shrink-0 rounded-full bg-emerald-100 px-3 py-1 text-sm font-bold text-emerald-700">{staff.resolvedCount}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </ChartCard>
          </div>
        </div>
      </div>
    </div>
  );
}
